import { useCallback, useEffect, useState, type RefObject } from 'react';
import { syncThemeColor } from './appearance.ts';

/**
 * Faerbt den Seitenhintergrund dunkel, solange eine Buehnenseite offen ist.
 *
 * Ohne das scheint auf iOS der weisse Body durch: Safari macht das Dokument
 * durch die ein- und ausfahrenden Leisten hoeher als `100vh`, und unterhalb
 * des dunklen Containers wird es weiss.
 */
export function useStageSurface() {
  useEffect(() => {
    // Auch am <html>: daran entscheidet styles.css, ob Hell/Dunkel ueber die
    // Papier- oder die Buehnenfarben laeuft.
    document.body.dataset.surface = 'stage';
    document.documentElement.dataset.surface = 'stage';
    syncThemeColor();
    return () => {
      delete document.body.dataset.surface;
      delete document.documentElement.dataset.surface;
      syncThemeColor();
    };
  }, []);
}

/**
 * Haelt den Bildschirm wach, solange `active` gilt.
 *
 * Auf dem Handy ist das keine Bequemlichkeit: sperrt sich das iPhone nach 30
 * Sekunden ohne Beruehrung, haelt Safari die Seite an -- Kamera, WebSocket und
 * alle Verbindungen stehen, und der Server beendet die Quelle nach der
 * Schonfrist. Wer sein Handy an die Strecke stellt, beruehrt es nicht mehr.
 *
 * Der Browser gibt die Sperre von selbst frei, sobald die Seite in den
 * Hintergrund geht; beim Zurueckkommen wird sie deshalb neu angefordert. Wo
 * es die Schnittstelle nicht gibt (aeltere Browser), bleibt es beim alten
 * Verhalten -- dann hilft nur „Automatische Sperre: Nie" in den Einstellungen.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let disposed = false;

    const acquire = async () => {
      if (disposed || lock || document.visibilityState !== 'visible') return;
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (disposed) {
          void sentinel.release().catch(() => undefined);
          return;
        }
        lock = sentinel;
        sentinel.addEventListener('release', () => {
          if (lock === sentinel) lock = null;
        });
      } catch {
        /* Energiesparmodus o. Ae. -- dann eben ohne. */
      }
    };

    void acquire();
    const onVisible = () => void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
      lock = null;
    };
  }, [active]);
}

/** true, solange die Media Query zutrifft -- folgt Drehen und Fenstergroesse. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export interface FullscreenApi {
  active: boolean;
  toggle: () => void;
  /** true, wenn der Browser echtes Vollbild fuer beliebige Elemente kann. */
  native: boolean;
}

/**
 * Vollbild fuer einen Bereich.
 *
 * iOS Safari kennt `requestFullscreen` nur fuer <video> selbst — dort waeren
 * unsere Einblendungen (Bild-im-Bild, Chat, Ton) weg. Deshalb gibt es einen
 * zweiten Weg: ein fest positionierter Bereich ueber der ganzen Seite. Der
 * sieht auf allen Geraeten gleich aus.
 */
export function useFullscreen(ref: RefObject<HTMLElement | null>): FullscreenApi {
  const native = typeof document !== 'undefined' && Boolean(document.fullscreenEnabled);
  const [nativeActive, setNativeActive] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const active = nativeActive || immersive;

  useEffect(() => {
    const onChange = () => setNativeActive(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [ref]);

  // Im Ersatzmodus uebernimmt Escape die Rolle des Browser-Ausstiegs.
  useEffect(() => {
    if (!immersive) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setImmersive(false);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [immersive]);

  const toggle = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (immersive) return setImmersive(false);
    if (document.fullscreenElement) return void document.exitFullscreen().catch(() => undefined);
    if (native) {
      void el.requestFullscreen().catch(() => setImmersive(true));
      return;
    }
    setImmersive(true);
  }, [ref, native, immersive]);

  return { active, toggle, native };
}
